/** 在模組子樹中依 key 尋找節點 */
export const findTreeNodeByKey = (node, key) => {
    if (!node || key == null) return null;
    if (node.key === key) return node;
    for (const child of node.children || []) {
        const found = findTreeNodeByKey(child, key);
        if (found) return found;
    }
    return null;
};

/** 收集節點底下所有子孫 key（不含自身） */
export const collectDescendantKeys = (node, out = []) => {
    (node?.children || []).forEach((child) => {
        out.push(child.key);
        collectDescendantKeys(child, out);
    });
    return out;
};

const cascadeDescendants = (root, key, select, moduleKeys, targetSet) => {
    const node = findTreeNodeByKey(root, key);
    if (!node?.children?.length) {
        return;
    }
    if (select) {
        targetSet.add(key);
        collectDescendantKeys(node).forEach((k) => {
            if (moduleKeys.has(k)) targetSet.add(k);
        });
    } else {
        targetSet.delete(key);
        collectDescendantKeys(node).forEach((k) => targetSet.delete(k));
    }
};

/**
 * 依「本次 tree 回傳」與「上一狀態」差異，對有子節點的項目做父子連動。
 * 不依賴 info.node（Ant Design Vue 常未帶入），避免父節點只勾自己。
 */
export const resolveModuleCheckedKeys = (
    root,
    eventCheckedKeys,
    previousCheckedKeys,
    moduleKeys
) => {
    const eventSet = new Set(
        (eventCheckedKeys || []).filter((k) => moduleKeys.has(k))
    );
    const prevSet = new Set(
        (previousCheckedKeys || []).filter((k) => moduleKeys.has(k))
    );
    const result = new Set(eventSet);

    const added = [...eventSet].filter((k) => !prevSet.has(k));
    const removed = [...prevSet].filter((k) => !eventSet.has(k));

    added.forEach((key) =>
        cascadeDescendants(root, key, true, moduleKeys, result)
    );
    removed.forEach((key) =>
        cascadeDescendants(root, key, false, moduleKeys, result)
    );

    return [...result];
};

/**
 * 使用 Ant Design Tree @check 的 info（含 node、checked）做父子連動。
 * 無 info 或無 node 時回傳 null，改走 resolveModuleCheckedKeys。
 */
export const applyToggleFromCheckInfo = (
    root,
    previousKeys,
    info,
    moduleKeys
) => {
    const toggledKey = info?.node?.key ?? info?.node?.eventKey;
    if (toggledKey == null || !moduleKeys.has(toggledKey)) {
        return null;
    }
    const node = findTreeNodeByKey(root, toggledKey);
    const set = new Set((previousKeys || []).filter((k) => moduleKeys.has(k)));
    if (info.checked) {
        set.add(toggledKey);
        if (node?.children?.length) {
            collectDescendantKeys(node).forEach((k) => {
                if (moduleKeys.has(k)) set.add(k);
            });
        }
    } else {
        set.delete(toggledKey);
        if (node?.children?.length) {
            collectDescendantKeys(node).forEach((k) => set.delete(k));
        }
    }
    return [...set];
};

/** 已勾選的父節點必須含齊模組內所有子孫 key（修正只勾父、子未亮） */
export const enforceParentCheckedWithDescendants = (root, keys, moduleKeys) => {
    const set = new Set((keys || []).filter((k) => moduleKeys.has(k)));
    [...set].forEach((key) => {
        const node = findTreeNodeByKey(root, key);
        if (!node?.children?.length) return;
        collectDescendantKeys(node).forEach((k) => {
            if (moduleKeys.has(k)) set.add(k);
        });
    });
    return [...set];
};

/** 所有子節點已勾選時，補勾父群組（如 group-system 無 code 的 UI 節點） */
export const enforceParentsWhenAllChildrenSelected = (
    root,
    keys,
    moduleKeys
) => {
    const set = new Set((keys || []).filter((k) => moduleKeys.has(k)));

    const walk = (node) => {
        (node?.children || []).forEach(walk);
        const childKeys = (node?.children || [])
            .map((c) => c.key)
            .filter((k) => k != null && moduleKeys.has(k));
        if (childKeys.length > 0 && childKeys.every((k) => set.has(k))) {
            set.add(node.key);
        }
    };

    walk(root);
    return [...set];
};

/**
 * 子孫未全選時移除父節點 key，避免送出 170000 等模組碼卻僅勾部分子頁（等同 super）。
 */
export const pruneIncompleteParentKeys = (root, keys, moduleKeys) => {
    const set = new Set((keys || []).filter((k) => moduleKeys.has(k)));

    const walk = (node) => {
        if (!node) return;
        (node.children || []).forEach(walk);

        const key = node.key;
        if (
            key == null ||
            !moduleKeys.has(key) ||
            !(node.children || []).length
        ) {
            return;
        }

        const descKeys = collectDescendantKeys(node).filter((k) =>
            moduleKeys.has(k)
        );
        if (descKeys.length === 0) {
            return;
        }

        const allDescSelected = descKeys.every((k) => set.has(k));
        if (!allDescSelected && set.has(key)) {
            set.delete(key);
        }
    };

    walk(root);
    return [...set];
};

/** 節點授權碼（優先 node.code，否則 6 位 key） */
const nodePermissionCode = (node) => {
    if (node?.code) return String(node.code);
    if (node?.key != null && /^\d{6}$/.test(String(node.key))) {
        return String(node.key);
    }
    return '';
};

/**
 * 功能閘門父碼：半選時仍應保留／送出。
 * 擁有父碼不會階層打開未勾子項（與 170000／xx0000 super 不同）。
 * 例：會員詳情 100110（子為 100111–100117）。
 */
export const PARTIAL_KEEP_FEATURE_PARENT_CODES = new Set(['100110']);

/**
 * 任一子孫已勾時補回功能閘門父碼（prune 半選父之後呼叫）。
 */
export const ensureFeatureParentsWhenAnyChildSelected = (
    root,
    keys,
    moduleKeys
) => {
    const set = new Set((keys || []).filter((k) => moduleKeys.has(k)));

    const walk = (node) => {
        if (!node) return;
        (node.children || []).forEach(walk);

        const code = nodePermissionCode(node);
        if (!code || !PARTIAL_KEEP_FEATURE_PARENT_CODES.has(code)) {
            return;
        }
        if (node.key == null || !moduleKeys.has(node.key)) {
            return;
        }

        const descKeys = collectDescendantKeys(node).filter((k) =>
            moduleKeys.has(k)
        );
        if (descKeys.some((k) => set.has(k))) {
            set.add(node.key);
        }
    };

    walk(root);
    return [...set];
};

/**
 * 模組內勾選狀態：子全選→補父；子未全選→移除父（僅半選顯示）；
 * 再補回需半選仍送出的功能閘門父碼（如 100110）。
 */
export const normalizeModuleCheckedKeys = (root, keys, moduleKeys) => {
    let normalized = enforceParentsWhenAllChildrenSelected(
        root,
        keys,
        moduleKeys
    );
    normalized = pruneIncompleteParentKeys(root, normalized, moduleKeys);
    normalized = ensureFeatureParentsWhenAnyChildSelected(
        root,
        normalized,
        moduleKeys
    );
    return normalized;
};

const collectModuleKeys = (root) => {
    const moduleKeys = new Set();
    const walk = (nodes = []) => {
        nodes.forEach((n) => {
            if (n?.key != null) moduleKeys.add(n.key);
            if (n?.children?.length) walk(n.children);
        });
    };
    walk([root]);
    return moduleKeys;
};

/** 各模組整理後再送出 permission_codes（剔除僅半選的 super 父；保留功能閘門父如 100110） */
export const preparePermissionSelectionForSubmit = (
    treeRoots = [],
    selectedKeys = []
) => {
    const merged = new Set(selectedKeys);
    (treeRoots || []).forEach((root) => {
        const moduleKeys = collectModuleKeys(root);
        const moduleSelected = [...merged].filter((k) => moduleKeys.has(k));
        let next = pruneIncompleteParentKeys(root, moduleSelected, moduleKeys);
        next = ensureFeatureParentsWhenAnyChildSelected(root, next, moduleKeys);
        moduleSelected.forEach((k) => merged.delete(k));
        next.forEach((k) => merged.add(k));
    });
    return Array.from(merged);
};

/**
 * checkStrictly 模式下將已選 key 拆成 checked / halfChecked，供 a-tree 顯示半選「-」。
 * 子孫未全選但父或部分子有選 → 父進 halfChecked；子孫全選 → 父進 checked。
 */
export const computeTreeCheckState = (root, keys, moduleKeys) => {
    const selected = new Set((keys || []).filter((k) => moduleKeys.has(k)));
    const checked = [];
    const halfChecked = [];

    const walk = (node) => {
        if (!node) return;
        (node.children || []).forEach(walk);

        const key = node.key;
        if (key == null || !moduleKeys.has(key)) return;

        const hasChildren = (node.children || []).length > 0;
        if (!hasChildren) {
            if (selected.has(key)) checked.push(key);
            return;
        }

        const descKeys = collectDescendantKeys(node).filter((k) =>
            moduleKeys.has(k)
        );
        const selectedDescCount = descKeys.filter((k) =>
            selected.has(k)
        ).length;
        const allDescSelected =
            descKeys.length > 0 && selectedDescCount === descKeys.length;

        if (allDescSelected) {
            checked.push(key);
        } else if (selectedDescCount > 0 || selected.has(key)) {
            halfChecked.push(key);
        }
    };

    walk(root);
    return { checked, halfChecked };
};

/** @deprecated 請用 resolveModuleCheckedKeys */
export const applyParentCascadeToCheckedKeys = (
    root,
    checkedKeys,
    info,
    moduleKeys
) => {
    const toggledKey = info?.node?.key ?? info?.node?.eventKey;
    if (toggledKey != null) {
        const checkedSet = new Set(
            (checkedKeys || []).filter((k) => moduleKeys.has(k))
        );
        cascadeDescendants(
            root,
            toggledKey,
            !!info.checked,
            moduleKeys,
            checkedSet
        );
        return [...checkedSet];
    }
    return resolveModuleCheckedKeys(root, checkedKeys, [], moduleKeys);
};

/** 從權限樹建立 key → 授權碼對照（含 tree_key、group-* 等非純數字 key） */
export const buildPermissionKeyToCodeMap = (treeRoots = []) => {
    const map = {};
    const walk = (nodes = []) => {
        (nodes || []).forEach((node) => {
            if (node?.key != null && node?.code) {
                map[String(node.key)] = String(node.code);
            }
            if (node?.children?.length) {
                walk(node.children);
            }
        });
    };
    walk(treeRoots);
    return map;
};

/** 樹上所有可指派授權碼（節點有 code 者，去重） */
export const collectAssignablePermissionCodes = (treeRoots = []) => {
    const codes = new Set();
    const walk = (nodes = []) => {
        (nodes || []).forEach((node) => {
            if (node?.code) {
                codes.add(String(node.code));
            }
            if (node?.children?.length) {
                walk(node.children);
            }
        });
    };
    walk(treeRoots);
    return codes;
};

/**
 * 將 a-tree 勾選的 key 轉為後端 permission_codes（permissions.name）。
 * 僅透過 key→code 對照，避免 tree_key 正則誤解析出不存在的代碼。
 */
export const keysToPermissionCodes = (
    keys = [],
    keyToCode = {},
    options = {}
) => {
    let allowed = null;
    if (options.allowedCodes instanceof Set) {
        allowed = options.allowedCodes.size > 0 ? options.allowedCodes : null;
    } else if (options.allowedCodes) {
        const set = new Set(options.allowedCodes);
        allowed = set.size > 0 ? set : null;
    }
    const codes = new Set();

    (keys || []).forEach((key) => {
        const code = keyToCode[String(key)];
        if (!code) {
            return;
        }
        if (allowed && !allowed.has(code)) {
            return;
        }
        codes.add(code);
    });

    return Array.from(codes);
};
